import React, { useState, useEffect } from "react";
import {
  Mail, Phone, Building2, User, Globe,
  Folder, Pencil, Plus
} from "lucide-react";
import AsyncSelect from "react-select/async";
import InstitutionNavbar from "./instituteNavbar";
import Footer from "../components/Footer";
import axios from "axios";
import toast from 'react-hot-toast';
import "../styles/GlobalStyles.css";
import SingleFieldEditModal from "./SingleFieldEditModal";

const ProfilePageInst = () => {
  const [institution, setInstitution] = useState({});
  const [previewImage, setPreviewImage] = useState(null); // Preview state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [postalError, setPostalError] = useState("");


  // Modal State
  const [modalState, setModalState] = useState({
    isOpen: false,
    field: null,
    title: '',
    value: ''
  });

  useEffect(() => {
    const fetchInstitutionProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("No auth token found.");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/profile/getInstProfile`,
          {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          }
        );
        // Ensure nested objects exist to prevent crashes
        const data = response.data?.Institute || {};
        if (!data.address) data.address = {};

        setInstitution(data);
      } catch (err) {
        console.error("Error fetching institution profile:", err);
        setError("Failed to fetch institution profile");
      } finally {
        setLoading(false);
      }
    };

    fetchInstitutionProfile();
  }, []);

  const handleChange = (field, value) => {
    setInstitution((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field, value) => {
    setInstitution((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      }
    }));
  };

  const handleSave = async () => {
    // Postal Code validation before save
    if (postalError) {
      toast.error("Please enter a valid 6-digit pincode before saving.");
      return;
    }

    if (
      institution.address?.postal_code &&
      institution.address.postal_code.length !== 6
    ) {
      toast.error("Pincode must be exactly 6 digits.");
      return;
    }

    try {
      const token = localStorage.getItem("token");

      let dataToSend;
      let headers = { Authorization: `Bearer ${token}` };

      if (institution.profilePicFile) {
        // Use FormData if there's a file
        dataToSend = new FormData();
        // Append all text fields
        Object.keys(institution).forEach(key => {
          if (key !== 'profilePicFile' && key !== 'address' && key !== '_id' && institution[key] !== null && institution[key] !== undefined) {
            dataToSend.append(key, institution[key]);
          }
        });

        // Handle Address separately as it's nested
        if (institution.address) {
          if (institution.address.street) dataToSend.append('street', institution.address.street);
          if (institution.address.city) dataToSend.append('city', institution.address.city);
          if (institution.address.area) dataToSend.append('area', institution.address.area);
          if (institution.address.state) dataToSend.append('state', institution.address.state);
          if (institution.address.postal_code) dataToSend.append('postal_code', institution.address.postal_code);
          if (institution.address.country) dataToSend.append('country', institution.address.country);
        }

        dataToSend.append("profile_pic", institution.profilePicFile);
        headers['Content-Type'] = 'multipart/form-data';

      } else {
        // Standard JSON update
        dataToSend = institution;
      }

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/profile/updateInstProfile/${institution._id}`,
        dataToSend,
        { headers }
      );

      if (response.data && response.data.data) {
        setInstitution(response.data.data);
        setPreviewImage(null); // Clear preview after save
      }
      toast.success("Profile updated successfully!");
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating profile:", err);
      toast.error("Failed to update profile.");
    }
  };

  const openEditModal = (field, title, value) => {
    setModalState({
      isOpen: true,
      field,
      title,
      value: value || ''
    });
  };

  const handleModalSave = async (field, value) => {
    try {
      const token = localStorage.getItem("token");

      // Update local state first for immediate feedback
      setInstitution(prev => ({ ...prev, [field]: value }));

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/profile/updateInstProfile/${institution._id}`,
        { ...institution, [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.data) {
        setInstitution(response.data.data);
      }

      toast.success(`${modalState.title} updated successfully!`);
    } catch (err) {
      console.error("Error updating field:", err);
      toast.error(`Failed to update ${modalState.title.toLowerCase()}.`);
      // Revert on error if needed, but for now we keep it simple
    }
  };

  if (loading) return <div className="loading-container"><span className="loading-text">Loading...</span></div>;
  if (error) return <div className="error-container"><span className="error-text">{error}</span></div>;

  return (
    <>
      <InstitutionNavbar />


      <div
        className="profile-page-container mt-[120px]"
      >

        <div className="profile-grid-layout">

          {/* Left Sidebar */}
          <div className="profile-sidebar">
            <div className="profile-card">
              <div className="sidebar-header">
                <div className="sidebar-title-group">
                  <User className="w-5 h-5 icon-yellow" />
                  <h3 className="section-header">Contact Person</h3>
                </div>
              </div>

              <div className="sidebar-contact-list">
                <div className="sidebar-contact-item group">
                  <div className="sidebar-contact-content">
                    <Mail className="w-4 h-4 icon-orange shrink-0" />
                    <span className="sidebar-contact-text" title={institution.email}>{institution.email}</span>
                  </div>
                  <Pencil
                    className="edit-icon"
                    onClick={() => openEditModal('email', 'Email Address', institution.email)}
                  />
                </div>

                <div className="sidebar-contact-item group">
                  <div className="sidebar-contact-content">
                    <Phone className="w-4 h-4 icon-yellow shrink-0" />
                    <span className="sidebar-contact-text">{institution.mobile_number}</span>
                  </div>
                  <Pencil
                    className="edit-icon"
                    onClick={() => openEditModal('mobile_number', 'Mobile Number', institution.mobile_number)}
                  />
                </div>
              </div>

            </div>


          </div>

          {/* Main Content */}
          <div className="profile-main-content">

            {/* Header Card */}
            <div className="profile-card relative">
              <div className="edit-wrapper" onClick={() => setIsEditing(!isEditing)}>
                <Pencil className={`w-4 h-4 ${isEditing ? 'edit-icon-active' : 'edit-icon'}`} />
              </div>

              <div className="profile-header-top">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{institution.name}</h2>
                  {/* <span className="status-incomplete">● Incomplete</span> */}
                </div>
                <div className="ml-auto text-xs text-gray-400 mr-8">
                  Last Edited: {institution.lastEdited ? new Date(institution.lastEdited).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                </div>
              </div>

              <div className="profile-main-info">
                <div className="institution-logo-placeholder relative group">
                  {previewImage ? (
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : institution.profile_pic ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${institution.profile_pic.startsWith('/') ? '' : '/'}${institution.profile_pic}`}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                      }}

                    />
                  ) : (
                    <Building2 className="w-8 h-8 icon-blue" />
                  )}

                  {isEditing && (
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center rounded-lg cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
                      <label htmlFor="profile-pic-upload" className="cursor-pointer text-white text-xs font-semibold">
                        Change
                      </label>
                      <input
                        id="profile-pic-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setInstitution(prev => ({ ...prev, profilePicFile: file }));
                            const reader = new FileReader();
                            reader.onload = (ev) => setPreviewImage(ev.target.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="institution-details">
                  <h3>{institution.name}</h3>
                  <p>{institution.type}</p>
                </div>
              </div>

              <div className="country-section">
                <Globe className="w-4 h-4 icon-orange" />
                <div>
                  <p className="country-label">Location</p>
                  <p className="country-value">
                    {[
                      institution.address?.area,
                      institution.address?.city,
                      institution.address?.country || 'India'
                    ]
                      .filter(Boolean)
                      .join(', ')
                    }
                  </p>
                </div>
              </div>
            </div>


            {/* Important Info Card */}
            {isEditing && (
              <div className="profile-card-large">
                <div className="form-section-header">
                  <Folder className="w-5 h-5 icon-yellow" fill="currentColor" />
                  <h3 className="text-lg font-bold text-blue-800">Important Information</h3>
                </div>

                <div className="form-grid-layout">
                  {/* Inst Name */}
                  <div className="form-group">
                    <label className="text-label-small">Name of Institution</label>
                    <input
                      disabled={!isEditing}
                      value={institution.name || ''}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className="profile-input-filled"
                    />
                  </div>

                  {/* Type */}
                  <div className="form-group">
                    <label className="text-label-small">Type of institution</label>
                    <select
                      disabled={!isEditing}
                      value={institution.type || ''}
                      onChange={(e) => handleChange('type', e.target.value)}
                      className="profile-input-filled"
                    >
                      <option value="">Select Type</option>
                      {['Pre-School', 'School', 'College', 'University', 'EdTech'].map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>

                  </div>

                  {/* Ownership */}
                  <div className="full-width form-group">
                    <label className="text-label-small">Type of Ownership</label>
                    <div className="radio-options">
                      <select
                        disabled={!isEditing}
                        value={institution.ownership || ''}
                        onChange={(e) => handleChange('ownership', e.target.value)}
                        className="profile-input-filled"
                      >
                        <option value="">Select Ownership</option>
                        {['Company', 'Franchise', 'School', 'Trust'].map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>

                    </div>
                  </div>

                  {/* Employee Strength */}
                  <div className="form-group">
                    <label className="text-label-small">Employee Strength</label>
                    <select
                      disabled={!isEditing}
                      value={institution.employeeCount || ''}
                      onChange={(e) => handleChange('employeeCount', e.target.value)}
                      className="profile-input-filled"
                    >
                      <option value="">Select Employee Strength</option>
                      <option value="1-10">1 to 10</option>
                      <option value="11-100">10 to 100</option>
                      <option value="100+">100+</option>
                    </select>

                  </div>

                  {/* Empty grid cell */}
                  <div className="hidden md:block"></div>

                  {/* Country */}
                  <div className="form-group">
                    <label className="text-label-small">Country</label>
                    <input
                      disabled
                      value="India"
                      className="profile-input-filled"
                    />

                  </div>

                  {/* City */}
                  <div className="form-group">
                    <label className="text-label-small">City</label>
                    <select
                      disabled={!isEditing}
                      value={institution.address?.city || ''}
                      onChange={(e) => {
                        const newCity = e.target.value;
                        setInstitution((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            city: newCity,
                            area: newCity === 'Bengaluru' ? prev.address.area : '' // Clear area if not Bengaluru
                          }
                        }));
                      }}
                      className="profile-input-filled"
                    >
                      <option value="">Select City</option>
                      {[
                        'Bagalkot', 'Ballari (Bellary)', 'Belagavi (Belgaum)', 'Bengaluru',
                        'Bidar', 'Chamarajanagar', 'Chikballapur', 'Chikkamagaluru (Chikmagalur)', 'Chitradurga', 'Dakshina Kannada',
                        'Davangere', 'Dharwad', 'Gadag', 'Hassan', 'Haveri', 'Kalaburagi (Gulbarga)', 'Kodagu (Coorg)', 'Kolar', 'Koppal',
                        'Mandya', 'Mysuru (Mysore)', 'Raichur', 'Ramanagara', 'Shivamogga (Shimoga)', 'Tumakuru (Tumkur)', 'Udupi',
                        'Uttara Kannada (Karwar)', 'Vijayapura (Bijapur)', 'Yadgir', 'Vijayanagara'
                      ].map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  {/* Address (Street) */}
                  <div className="form-group">
                    <label className="text-label-small">Street</label>
                    <input
                      disabled={!isEditing}
                      value={institution.address?.street || ''}
                      onChange={(e) => handleAddressChange('street', e.target.value)}
                      placeholder="Enter street..."
                      className="profile-input-filled"
                    />
                  </div>

                  {/* State */}
                  <div className="form-group">
                    <label className="text-label-small">State</label>
                    <input
                      disabled
                      value="Karnataka"
                      className="profile-input-filled"
                    />

                  </div>

                  {/* Area */}
                  <div className="form-group">
                    <label className="text-label-small">
                      Area {(institution.address?.city !== 'Bengaluru' && institution.address?.city) && <span className="text-xs text-gray-400 font-normal ml-1">(Not required for selected city)</span>}
                    </label>
                    {isEditing ? (
                      <AsyncSelect
                        isDisabled={institution.address?.city !== 'Bengaluru'}
                        cacheOptions
                        // Key prop forces component reset when pincode changes (to re-filter options)
                        key={institution.address?.postal_code || 'area-select'}
                        loadOptions={async (inputValue) => {
                          // If Pincode is set, narrow search to that pin content (fetched via a new param or just by client filtering, 
                          // but to be safe and "industry", we search backend by pin OR query.
                          // Actually, our Plan says: Enter Pin -> Filter Dropdown.

                          // If text input is too short
                          if (!inputValue && !institution.address?.postal_code) return [];

                          try {
                            const query = inputValue || institution.address?.postal_code || '';
                            const res = await axios.get(
                              `${process.env.REACT_APP_API_URL}/api/locations/search?query=${query}`
                            );

                            let locations = res.data.locations || [];

                            // If we have a postal code already, filter results to match it
                            if (institution.address?.postal_code && !inputValue) {
                              // If user hasn't typed anything yet but has a pincode, show ALL areas for that pincode
                              locations = locations.filter(l => l.pincode === institution.address.postal_code);
                            }

                            return locations.map((loc) => ({
                              value: loc,
                              label: loc.area,
                            }));
                          } catch (error) {
                            console.error("Error fetching locations:", error);
                            return [];
                          }
                        }}
                        defaultOptions
                        value={
                          institution.address?.area
                            ? {
                              label: institution.address.area,
                              value: { area: institution.address.area, pincode: institution.address.postal_code }
                            }
                            : null
                        }
                        onChange={(selectedOption) => {
                          const loc = selectedOption ? selectedOption.value : null;
                          if (loc) {
                            setInstitution((prev) => ({
                              ...prev,
                              address: {
                                ...prev.address,
                                area: loc.area,
                                // AUTOFILL: Update pincode if available
                                postal_code: loc.pincode || prev.address.postal_code
                              }
                            }));
                          } else {
                            handleAddressChange('area', '');
                          }
                        }}
                        placeholder={
                          institution.address?.postal_code
                            ? `Select area in ${institution.address.postal_code}...`
                            : (institution.address?.city !== 'Bengaluru' ? "Select Bengaluru to enable area search" : "Search for area...")
                        }
                        isClearable
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: '44px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: state.isDisabled ? '#E5E7EB' : '#F3F4F6',
                            boxShadow: 'none',
                            fontSize: '0.95rem',
                            opacity: state.isDisabled ? 0.7 : 1
                          }),
                          input: (base) => ({
                            ...base,
                            color: '#1F2937'
                          }),
                          singleValue: (base) => ({
                            ...base,
                            color: '#1F2937'
                          }),
                          placeholder: (base) => ({
                            ...base,
                            color: '#9CA3AF'
                          })
                        }}
                      />
                    ) : (
                      <input
                        disabled
                        value={institution.address?.area || ''}
                        className="profile-input-filled"
                      />
                    )}
                  </div>

                  {/* Pincode */}

                  <div className="form-group">
                    <label className="text-label-small">Pincode</label>

                    <input
                      disabled={!isEditing}
                      type="text"
                      placeholder="Enter 6-digit PIN code"
                      value={institution.address?.postal_code || ''}
                      onChange={(e) => {
                        let value = e.target.value;

                        setPostalError("");
                        // Allow only digits
                        value = value.replace(/\D/g, "");

                        // Limit to 6 digits
                        if (value.length > 6) return;

                        const newPincode = value;

                        // Validate length
                        if (value.length === 6) {
                          setPostalError("");
                        } else if (value.length > 0) {
                          setPostalError("Pincode must be exactly 6 digits.");
                        }

                        setInstitution((prev) => ({
                          ...prev,
                          address: {
                            ...prev.address,
                            postal_code: newPincode,
                          },
                        }));

                        // Direct Autofill: Fetch area if valid pincode
                        if (newPincode.length === 6) {
                          axios.get(`${process.env.REACT_APP_API_URL}/api/locations/search?query=${newPincode}`)
                            .then(res => {
                              const locs = res.data.locations || [];
                              if (locs.length > 0) {
                                setInstitution(prev => ({
                                  ...prev,
                                  address: {
                                    ...prev.address,
                                    area: locs[0].area // Auto-select the first one
                                  }
                                }));
                              }
                            })
                            .catch(err => console.error("Error fetching area for pincode:", err));
                        }
                      }}
                      className="profile-input-filled"
                      style={{
                        borderColor: postalError ? "#dc2626" : "#d1d5db",
                        backgroundColor: postalError ? "#fef2f2" : "#F3F4F6",
                      }}
                    />

                    {postalError && (
                      <div style={{ color: "#dc2626", fontSize: "0.85rem", marginTop: "4px" }}>
                        {postalError}
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}

            {/* Additional Info Card */}
            {isEditing && (
              <div className="profile-card-large">
                <div className="form-section-header">
                  <Plus className="w-5 h-5 text-blue-800 font-bold" />
                  <h3 className="text-lg font-bold text-blue-800">Additional Information</h3>
                </div>

                <div className="additional-info-container">
                  <div className="form-group">
                    <label className="text-label-small">URL link</label>
                    <input
                      disabled={!isEditing}
                      value={institution.website || ''}
                      onChange={(e) => handleChange('website', e.target.value)}
                      placeholder="https://"
                      className="profile-input-filled"
                    />
                  </div>

                  <div className="form-group">
                    <label className="text-label-small">Brief Description</label>
                    <textarea
                      disabled={!isEditing}
                      value={institution.description || ''}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Tell us something you would want the employees to know!"
                      rows={4}
                      className="profile-input-filled resize-none"
                    />
                    <div className="text-counter">800/800 Characters remaining</div>
                  </div>
                </div>

                <button
                  onClick={handleSave}
                  className="btn-save-primary"
                >
                  Save
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
      {/* Footer */}
      <Footer />

      <SingleFieldEditModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        onSave={handleModalSave}
        title={modalState.title}
        initialValue={modalState.value}
        fieldName={modalState.field}
      />
    </>
  );
};

export default ProfilePageInst;
